import { createContext, useContext, useRef, useCallback, type PropsWithChildren } from 'react'
import * as THREE from 'three'

import { noop } from '@/lib/utils'

type MapNode = THREE.Object3D | Element
type RegisterFn = (name: string, object: MapNode | undefined) => void

interface RegistryContextType {
  register: RegisterFn
  getRegistry: () => Map<string, MapNode> | undefined
}

const RegistryContext = createContext<RegistryContextType>({
  getRegistry: () => undefined,
  register: noop,
})

// eslint-disable-next-line react-refresh/only-export-components
export function useRegisterShelf(name: string) {
  const { register } = useContext(RegistryContext)

  return useCallback((node: MapNode | null) => {
    if (node === null) return noop

    register(name, node)

    return () => register(name, undefined)
  }, [name, register])
}

// eslint-disable-next-line react-refresh/only-export-components
export function useShelfRegistry<T extends MapNode>() {
  const { getRegistry } = useContext(RegistryContext)
  const registry = getRegistry() as Map<string, T> | undefined

  if (!registry) throw new Error('useShelfRegistry must be used within a ShelfRegistry provider')

  return registry
}

function ShelfRegistry({ children }: PropsWithChildren) {
  const registry = useRef<Map<string, MapNode>>(new Map())

  const register = useCallback<RegisterFn>((name: string, object: MapNode | undefined): void => {
    if (object !== undefined) {
      registry.current.set(name, object)
    } else {
      registry.current.delete(name)
    }
  }, [])

  const getRegistry = useCallback(() => registry.current, [])

  return (
    <RegistryContext.Provider value={{ register, getRegistry }}>
      {children}
    </RegistryContext.Provider>
  )
}

export default ShelfRegistry